import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { COMPARISON_ROWS } from "../../data/landingContent";
import { VAULT } from "../../data/vaultStats";

const STEPS = [
  ["01", "Pick one route", "Filter by skill, budget and time. Commit to one playbook instead of chasing every AI idea."],
  ["02", "Run prompts in order", "Move from niche and offer through production, pricing, launch and follow-up."],
  ["03", "Ship something sellable", "Finish with a product, content engine or service package you can put in front of a buyer."],
];

export const LandingNarrative = ({ lifetimeHref }) => (
  <section id="about" className="scroll-mt-24 border-b border-white/10 bg-[#030206]" data-testid="landing-about">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:px-10 lg:py-28">
      <div className="max-w-4xl">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300">The real problem</div>
        <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] text-white sm:text-4xl lg:text-5xl" data-testid="about-heading">
          AI can make almost anything. Most people still do not know what to build first.
        </h2>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-zinc-300 sm:text-lg">
          Stag Vault removes the blank page. Each track is an ordered route from idea to offer, so you always know the next prompt to run and the output it should produce.
        </p>
      </div>

      <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 lg:grid-cols-12">
        <div className="bg-[#09080b] p-6 md:p-8 lg:col-span-5" data-testid="landing-not-a-course">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">What keeps people stuck</div>
          <h3 className="mt-3 font-display text-2xl font-bold text-white">More information. No sequence.</h3>
          <ul className="mt-6 space-y-4">
            {["A folder of unrelated one-off prompts", "Another long course with no finished asset", "Advice spread across newsletters, videos and saved posts"].map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-zinc-400"><X size={16} className="mt-0.5 shrink-0 text-rose-300" />{item}</li>
            ))}
          </ul>
        </div>
        <div className="bg-[#0c0910] p-6 md:p-8 lg:col-span-7" data-testid="about-pillar-different">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">What changes inside the vault</div>
          <h3 className="mt-3 font-display text-2xl font-bold text-white">One income model. One ordered build path.</h3>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {["Prompts organised around a real commercial outcome", "Clear outputs for every step, not vague inspiration", "Run with GPT-5.2 inside the app or copy anywhere", "Future tracks included in the one-time price"].map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-zinc-300"><Check size={16} className="mt-0.5 shrink-0 text-emerald-300" />{item}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-16" data-testid="landing-free-comparison">
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300">Why not just Google prompts?</div>
          <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] text-white sm:text-4xl">The value is not the prompt. It is knowing what comes next.</h2>
        </div>
        <div className="mt-8 overflow-hidden border border-zinc-800">
          <div className="hidden grid-cols-12 border-b border-zinc-800 bg-zinc-900/70 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 sm:grid">
            <div className="col-span-3">Compare</div><div className="col-span-4">Free prompt search</div><div className="col-span-5 text-purple-300">Stag Vault</div>
          </div>
          {COMPARISON_ROWS.map(([label, free, vault]) => (
            <div key={label} className="grid gap-2 border-b border-zinc-900 bg-[#08070a] px-5 py-5 last:border-b-0 sm:grid-cols-12 sm:gap-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 sm:col-span-3">{label}</div>
              <div className="text-sm text-zinc-500 sm:col-span-4">{free}</div>
              <div className="text-sm text-zinc-200 sm:col-span-5">{vault}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 border-t border-zinc-800 pt-12" data-testid="landing-system-flow">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300">How it works</div>
        <div className="mt-7 grid gap-8 md:grid-cols-3">
          {STEPS.map(([number, title, body], index) => (
            <div key={number} className="border-l border-purple-400/40 pl-5" data-testid={`landing-step-${index + 1}`}>
              <div className="font-mono text-xs text-purple-300">{number}</div>
              <h3 className="mt-3 font-display text-xl font-bold text-white">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{body}</p>
            </div>
          ))}
        </div>
        <Link to={lifetimeHref} className="mt-10 inline-flex min-h-12 items-center gap-3 border border-purple-400 bg-purple-400 px-5 text-xs font-black uppercase tracking-[0.16em] text-black transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-purple-300" data-testid="landing-narrative-lifetime-cta">
          Unlock all {VAULT.tracks} playbooks · £25 <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  </section>
);